<template>
  <v-dialog
    v-model="dialog"
    :fullscreen="$vuetify.display.smAndDown"
    max-width="700"
    class="upload-dialog"
  >
    <v-card class="d-flex flex-column h-100">
      <v-card-title class="upload-dialog__title">
        <h1 class="text-h6">{{ $t('ad-materials.upload.title') }}</h1>
        <v-btn
          variant="text"
          icon="mdi-close"
          color="white"
          @click="closeDialog"
        />
      </v-card-title>

      <v-card-text class="upload-dialog__content flex-grow-1">
        <!-- Step indicators -->
        <UploadStepper
          :current-step="currentStep"
          :steps="steps"
          class="mb-8"
        />

        <!-- Step content -->
        <div>
          <!-- Step 1: File selection -->
          <div v-if="currentStep === 1">
            <div
              ref="dropZone"
              class="drop-zone"
              :class="{ 'drop-zone--active bg-primary-lighten-5': isDragging }"
              @dragover.prevent="onDragOver"
              @dragleave.prevent="onDragLeave"
              @dragenter.prevent="onDragEnter"
              @drop.prevent="onDrop"
              @click="triggerFileInput"
            >
              <div class="text-center">
                <v-icon
                  class="mb-4"
                  size="64"
                  color="primary"
                  >mdi-cloud-upload</v-icon
                >
                <h3 class="text-body-1">
                  {{ $t('ad-materials.upload.drag-drop') }}
                  <span class="text-primary cursor-pointer">{{
                    $t('ad-materials.upload.browse')
                  }}</span>
                </h3>
                <p class="text-body-2 text-medium-emphasis mt-2">
                  {{ $t('ad-materials.upload.supported-formats') }}
                </p>
                <p class="text-caption text-medium-emphasis mt-1">
                  {{ $t('ad-materials.upload.max-file-size') }}
                </p>
              </div>
            </div>

            <input
              ref="fileInput"
              type="file"
              :accept="acceptAttribute"
              multiple
              class="d-none"
              @change="onFileSelect"
            />

            <!-- Selected files list -->
            <div
              v-if="selectedFiles.length > 0"
              class="selected-files-container mt-4"
            >
              <div
                v-for="(file, index) in selectedFiles"
                :key="index"
                class="selected-file px-3 py-3 mb-4 border bg-grey-lighten-4"
              >
                <div class="d-flex align-center file-item-row">
                  <div class="file-info d-flex align-center">
                    <img
                      v-if="file.type.startsWith('image/')"
                      :src="getPreviewUrl(file)"
                      :alt="$t('ad-materials.upload.preview-alt')"
                      class="file-thumbnail mr-2 flex-shrink-0"
                    />
                    <v-icon
                      v-else
                      class="mr-2 flex-shrink-0"
                      color="primary"
                    >
                      {{ getFileIcon(file) }}
                    </v-icon>
                    <span class="file-name text-truncate">{{ file.name }}</span>
                  </div>
                  <div class="file-actions flex-shrink-0 ml-2">
                    <v-btn
                      icon
                      variant="text"
                      color="error"
                      size="small"
                      @click="removeFile(index)"
                    >
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>

            <!-- Validation error lists -->
            <v-alert
              v-if="invalidUnsupportedFiles.length > 0"
              type="error"
              variant="tonal"
              class="mt-4"
            >
              <template #prepend>
                <v-icon color="error">mdi-close-circle</v-icon>
              </template>
              <div class="text-subtitle-1 font-weight-bold mb-2">
                {{ $t('ad-materials.upload.unsupported-files') }}
              </div>
              <div
                v-for="(name, idx) in invalidUnsupportedFiles"
                :key="`uns-${idx}`"
                class="text-error"
              >
                - {{ name }}
              </div>
            </v-alert>

            <v-alert
              v-if="invalidTooLargeFiles.length > 0"
              type="error"
              variant="tonal"
              class="mt-4"
            >
              <template #prepend>
                <v-icon color="error">mdi-alert-circle</v-icon>
              </template>
              <div class="text-subtitle-1 font-weight-bold mb-2">
                {{ $t('ad-materials.upload.too-large-files') }}
              </div>
              <div
                v-for="(name, idx) in invalidTooLargeFiles"
                :key="`big-${idx}`"
                class="text-error"
              >
                - {{ name }}
              </div>
            </v-alert>

            <!-- Action buttons for step 1 -->
            <div class="d-flex justify-end mt-6">
              <v-btn
                color="primary"
                :disabled="selectedFiles.length === 0"
                @click="goToStep2"
              >
                {{ $t('ad-materials.upload.next') }}
              </v-btn>
            </div>
          </div>

          <!-- Step 2: Topic and Asset Type selection -->
          <div v-if="currentStep === 2">
            <h3 class="text-h6 mb-5">{{ $t('ad-materials.upload.choose-topic-and-type') }}</h3>
            <div
              v-for="(file, index) in selectedFiles"
              :key="index"
              class="mb-4"
            >
              <v-select
                v-model="fileTopics[index]"
                :items="props.topics"
                :loading="topicsLoading"
                item-title="name"
                item-value="id"
                return-object
                variant="outlined"
                :label="$t('ad-materials.upload.select-topic')"
                hide-details
                class="mb-2"
              />

              <v-select
                v-model="fileAssetTypes[index]"
                :items="assetTypeOptions"
                item-title="label"
                item-value="value"
                variant="outlined"
                :label="$t('ad-materials.upload.select-asset-type')"
                hide-details
                class="mb-2"
              />

              <div class="d-flex align-center file-container mt-2 border bg-grey-lighten-4">
                <div class="file-preview mr-3 bg-grey-lighten-3">
                  <img
                    v-if="file.type.startsWith('image/')"
                    :src="getPreviewUrl(file)"
                    alt="Preview"
                    class="preview-image"
                  />
                  <div
                    v-else
                    class="file-icon"
                  >
                    <v-icon
                      color="primary"
                      size="large"
                    >
                      {{ getFileIcon(file) }}
                    </v-icon>
                  </div>
                </div>
                <div class="flex-grow-1">{{ file.name }}</div>
              </div>
            </div>

            <!-- Action buttons for step 2 -->
            <div class="d-flex justify-end mt-6">
              <v-btn
                color="secondary"
                variant="text"
                class="mr-3"
                @click="currentStep = 1"
              >
                {{ $t('ad-materials.upload.back') }}
              </v-btn>
              <v-btn
                color="primary"
                :disabled="!allFilesHaveTopicAndAssetType || isUploading"
                @click="startUpload"
              >
                {{ $t('ad-materials.upload.upload') }}
              </v-btn>
            </div>
          </div>

          <!-- Step 3: Upload progress and success -->
          <div v-if="currentStep === 3">
            <!-- Upload in progress -->
            <div
              v-if="isUploading"
              class="text-center my-10 py-6"
            >
              <v-progress-circular
                :model-value="displayProgress"
                color="primary"
                size="150"
                width="10"
                class="my-10"
              >
                {{ displayProgress }}%
              </v-progress-circular>

              <div class="text-h6 mt-8">
                {{ $t('ad-materials.upload.please-wait') }}
              </div>
            </div>

            <!-- Upload success indicator -->
            <div
              v-else-if="uploadComplete && !uploadError"
              class="text-center mt-10"
            >
              <div class="success-icon-circle bg-primary">
                <v-icon
                  color="white"
                  size="64"
                  >mdi-check</v-icon
                >
              </div>

              <div class="text-h5 mt-8 mb-10">
                {{ $t('ad-materials.upload.successfully-uploaded') }}
              </div>

              <div class="d-flex justify-end">
                <v-btn
                  color="primary"
                  @click="closeDialog"
                >
                  {{ $t('ad-materials.upload.done') }}
                </v-btn>
              </div>
            </div>

            <!-- Upload error indicator -->
            <div
              v-else-if="props.uploadError"
              class="text-center my-10 py-6"
            >
              <div class="error-icon-circle bg-error">
                <v-icon
                  color="white"
                  size="64"
                  >mdi-close</v-icon
                >
              </div>

              <div class="text-h5 mt-8 mb-6">
                {{ $t('ad-materials.upload.upload-failed') }}
              </div>

              <div class="text-body-1 mb-10 text-medium-emphasis">
                {{ props.uploadError }}
              </div>

              <div class="d-flex justify-end">
                <v-btn
                  color="secondary"
                  variant="text"
                  class="mr-3"
                  @click="currentStep = 2"
                >
                  {{ $t('ad-materials.upload.try-again') }}
                </v-btn>
                <v-btn
                  color="primary"
                  @click="closeDialog"
                >
                  {{ $t('ad-materials.upload.close') }}
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import * as Sentry from '@sentry/vue'
import { computed, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { showSnackbarMessage } from '@/lib/snackbarMessages'

import { getAllAssetTypes } from './assetTypes'
import { getAcceptAttribute } from './fileTypes'
import UploadStepper from './UploadStepper.vue'

// Get accept attribute from helper
const acceptAttribute = getAcceptAttribute()

const { t: _t } = useI18n()

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  topics: {
    type: Array,
    default: () => [],
  },
  topicsLoading: {
    type: Boolean,
    default: false,
  },
  uploadProgress: {
    type: Number,
    default: 0,
  },
  uploadComplete: {
    type: Boolean,
    default: false,
  },
  uploadError: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['update:modelValue', 'upload-start', 'upload-complete'])

// Step configuration
const steps = [
  { number: 1, displayNumber: '01', labelKey: 'ad-materials.upload.step1' },
  { number: 2, displayNumber: '02', labelKey: 'ad-materials.upload.step2' },
  { number: 3, displayNumber: '03', labelKey: 'ad-materials.upload.step3' },
]

// Security: Explicitly forbidden extensions regardless of mimetype
const FORBIDDEN_EXTENSIONS = new Set([
  '.exe',
  '.bat',
  '.sh',
  '.js',
  '.jar',
  '.com',
  '.msi',
  '.zip',
  '.rar',
  '.7z',
  '.vbs',
  '.ps1',
  '.xlsm',
])

// Allowed mimetypes grouped by category (mirror backend)
const ALLOWED_MIME_TYPES = {
  image: new Set([
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/svg+xml',
    'image/tiff',
    'image/gif',
  ]),
  video: new Set(['video/mp4', 'video/quicktime', 'video/webm']),
  document: new Set([
    'application/pdf',
    'text/plain',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.oasis.opendocument.text',
  ]),
}

// Per-category size limits (bytes)
const SIZE_LIMITS = {
  image: 20 * 1024 * 1024,
  video: 200 * 1024 * 1024,
  document: 10 * 1024 * 1024,
}

function getFileExtension(filename = '') {
  const lower = String(filename).toLowerCase()
  const idx = lower.lastIndexOf('.')
  return idx >= 0 ? lower.substring(idx) : ''
}

function hasForbiddenExtension(filename = '') {
  const ext = getFileExtension(filename)
  return FORBIDDEN_EXTENSIONS.has(ext)
}

function getCategoryFromMimetypeOrName(mimetype, filename) {
  if (!mimetype) return null
  if (hasForbiddenExtension(filename)) return null
  if (ALLOWED_MIME_TYPES.image.has(mimetype)) return 'image'
  if (ALLOWED_MIME_TYPES.video.has(mimetype)) return 'video'
  if (ALLOWED_MIME_TYPES.document.has(mimetype)) return 'document'
  return null
}

// Dialog state
const dialog = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

// Steps:
// 1 = file selection
// 2 = topic selection
// 3 = uploading/success
const currentStep = ref(1)
const selectedFiles = ref([])
const fileInput = ref(null)
const dropZone = ref(null)
const isDragging = ref(false)
const isUploading = ref(false)

// Topic and asset type selection state
const fileTopics = ref([])
const fileAssetTypes = ref([])
const filePreviewUrls = ref({})

// Get all asset types for the dropdown
const assetTypeOptions = getAllAssetTypes(_t)

// New: per-file invalid lists
const invalidUnsupportedFiles = ref([])
const invalidTooLargeFiles = ref([])

// Check if all files have a topic and asset type assigned
const allFilesHaveTopicAndAssetType = computed(() => {
  if (selectedFiles.value.length === 0) return false
  return (
    fileTopics.value.every((topic) => !!topic) &&
    fileAssetTypes.value.every((assetType) => !!assetType)
  )
})

// Smooth progress animation
const smoothProgress = ref(0)
const targetProgress = ref(0)
let animationFrameId = null
let uploadStartTime = null
const MINIMUM_DURATION = 1500 // 1.5 seconds minimum

const animateProgress = () => {
  if (!uploadStartTime) uploadStartTime = performance.now()

  const elapsed = performance.now() - uploadStartTime

  if (targetProgress.value >= 100 && elapsed < MINIMUM_DURATION) {
    // Upload completed but enforce minimum duration; ease from current to 100
    const diff = 100 - smoothProgress.value
    smoothProgress.value += diff * 0.15
  } else if (targetProgress.value > 0) {
    // Real progress from parent - smooth easing
    const diff = targetProgress.value - smoothProgress.value
    if (Math.abs(diff) < 0.1) {
      smoothProgress.value = targetProgress.value
      if (targetProgress.value >= 100 && elapsed >= MINIMUM_DURATION) return
    } else {
      smoothProgress.value += diff * 0.15
    }
  } else {
    // Optimistic progress 0-90% over 3 seconds
    const t = Math.min(elapsed / 3000, 1)
    const eased = 1 - Math.pow(1 - t, 3)
    smoothProgress.value = eased * 90
  }

  animationFrameId = requestAnimationFrame(animateProgress)
}

// Computed progress for display
const displayProgress = computed(() => Math.round(smoothProgress.value))

// Watch for real progress updates
watch(
  () => props.uploadProgress,
  (newProgress) => {
    targetProgress.value = newProgress
  }
)

// Watch for upload completion
watch(
  () => props.uploadComplete,
  (isComplete) => {
    if (isComplete && isUploading.value) {
      targetProgress.value = 100
      // Let the animator finish smoothly; honor MINIMUM_DURATION
      const elapsed = uploadStartTime ? performance.now() - uploadStartTime : 0
      const remaining = Math.max(0, MINIMUM_DURATION - elapsed)
      setTimeout(() => {
        isUploading.value = false
        smoothProgress.value = 100
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId)
          animationFrameId = null
        }
      }, remaining)
    }
  }
)

// Watch for upload errors
watch(
  () => props.uploadError,
  (error) => {
    if (error) {
      showSnackbarMessage('error', error)
      isUploading.value = false
      // Reset to step 2 to allow retry
      currentStep.value = 2
    }
  }
)

// Close dialog and reset state
const closeDialog = () => {
  dialog.value = false
  resetState()
}

// Reset all state when dialog closes
const resetState = () => {
  currentStep.value = 1
  selectedFiles.value = []
  fileTopics.value = []
  fileAssetTypes.value = []
  isUploading.value = false
  smoothProgress.value = 0
  targetProgress.value = 0
  uploadStartTime = null
  invalidUnsupportedFiles.value = []
  invalidTooLargeFiles.value = []

  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
    animationFrameId = null
  }

  // Clean up preview URLs
  Object.values(filePreviewUrls.value).forEach((url) => {
    URL.revokeObjectURL(url)
  })
  filePreviewUrls.value = {}
}

// Handle drag events
const onDragEnter = () => {
  isDragging.value = true
}

const onDragOver = () => {
  isDragging.value = true
}

const onDragLeave = () => {
  isDragging.value = false
}

// Handle file drop
const onDrop = (event) => {
  isDragging.value = false
  const files = event.dataTransfer.files
  if (files.length > 0) {
    addFiles(files)
  }
}

// Trigger file input
const triggerFileInput = () => {
  fileInput.value.click()
}

// Handle file selection
const onFileSelect = (event) => {
  const files = event.target.files
  if (files.length > 0) {
    addFiles(files)
  }
  // Reset file input to allow selecting the same files again
  event.target.value = null
}

// Add files to the selected files list
const addFiles = (files) => {
  for (let i = 0; i < files.length; i++) {
    const file = files[i]

    // Security: reject explicitly forbidden extensions
    if (hasForbiddenExtension(file.name)) {
      // Collect as unsupported
      invalidUnsupportedFiles.value.push(file.name)
      Sentry.captureMessage(`Forbidden extension: ${file.name}`, {
        level: 'warning',
        tags: { module: 'ad-materials', action: 'file-upload' },
        extra: { fileName: file.name },
      })
      continue
    }

    const category = getCategoryFromMimetypeOrName(file.type, file.name)

    if (!category) {
      // Collect as unsupported
      invalidUnsupportedFiles.value.push(file.name)
      Sentry.captureMessage(`Unsupported file type: ${file.type} (${file.name})`, {
        level: 'warning',
        tags: { module: 'ad-materials', action: 'file-upload' },
        extra: { fileName: file.name, mimetype: file.type },
      })
      continue
    }

    const maxSize = SIZE_LIMITS[category]
    if (file.size > maxSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1)
      // Collect as too large
      invalidTooLargeFiles.value.push(file.name)

      Sentry.captureMessage(`File too large: ${file.name} (${fileSizeMB} MB)`, {
        level: 'warning',
        tags: {
          module: 'ad-materials',
          action: 'file-upload',
          category,
        },
        extra: {
          fileName: file.name,
          fileSize: file.size,
          fileSizeMB: fileSizeMB,
          maxSizeBytes: maxSize,
        },
      })
      continue
    }

    // Accept file
    selectedFiles.value.push(file)

    // Initialize topic and asset type as null for this file
    fileTopics.value.push(null)
    fileAssetTypes.value.push(null)

    // Generate preview URL for images
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file)
      filePreviewUrls.value[file.name] = url
    }
  }
}

// Remove file from selected files
const removeFile = (index) => {
  // Release object URL if it exists
  const file = selectedFiles.value[index]
  if (file && file.type.startsWith('image/') && filePreviewUrls.value[file.name]) {
    URL.revokeObjectURL(filePreviewUrls.value[file.name])
    delete filePreviewUrls.value[file.name]
  }

  selectedFiles.value.splice(index, 1)
  fileTopics.value.splice(index, 1)
  fileAssetTypes.value.splice(index, 1)
}

// Get appropriate icon for file type
const getFileIcon = (file) => {
  const type = file.type
  if (type.startsWith('image/')) {
    return 'mdi-file-image-outline'
  } else if (type.startsWith('video/')) {
    return 'mdi-file-video-outline'
  } else if (
    type === 'application/pdf' ||
    type === 'text/plain' ||
    type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    type === 'application/vnd.oasis.opendocument.text'
  ) {
    return 'mdi-file-document-outline'
  } else {
    return 'mdi-file-outline'
  }
}

// Get preview URL for a file
const getPreviewUrl = (file) => {
  if (file.type.startsWith('image/') && filePreviewUrls.value[file.name]) {
    return filePreviewUrls.value[file.name]
  }
  return null
}

// Go to step 2 (topic selection)
const goToStep2 = () => {
  currentStep.value = 2
}

// Start upload process
const startUpload = () => {
  if (selectedFiles.value.length === 0 || !allFilesHaveTopicAndAssetType.value) return

  currentStep.value = 3
  isUploading.value = true
  smoothProgress.value = 0
  targetProgress.value = 0
  uploadStartTime = null

  // Start animation immediately
  animateProgress()

  // Create an array of file/topic/assetType tuples
  const uploads = selectedFiles.value.map((file, index) => ({
    file,
    topic: fileTopics.value[index],
    assetType: fileAssetTypes.value[index],
  }))

  // Emit upload-start event to parent
  emit('upload-start', { uploads })
}

// Clean up object URLs when component is unmounted
onUnmounted(() => {
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
  }

  Object.values(filePreviewUrls.value).forEach((url) => {
    URL.revokeObjectURL(url)
  })
})
</script>

<style scoped>
.upload-dialog,
.upload-dialog.v-dialog--fullscreen {
  z-index: 1201 !important;
}

.upload-dialog__title {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
  background-color: rgb(var(--v-theme-primary));
}

.upload-dialog__content {
  padding: 24px 16px;
}
.drop-zone {
  border: 2px dashed #175e51;
  border-radius: 24px;
  padding: 20px 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #eef4f4;
}

.drop-zone--active {
  border-color: #175e51;
}

.cursor-pointer {
  cursor: pointer;
}

/* Success icon styling */
.success-icon-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

/* Error icon styling */
.error-icon-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

/* File preview styling for step 1 */
.selected-files-container {
  border-radius: 8px;
  max-height: 400px;
  overflow-y: auto;
}

.selected-file {
  border-radius: 4px;
}

.file-thumbnail {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 4px;
}

/* File item layout */
.file-item-row {
  gap: 8px;
}

.file-info {
  flex: 1;
  min-width: 0; /* Important: allows text truncation to work */
  overflow: hidden;
}

.file-name {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-actions {
  width: 40px; /* Fixed width for close button */
}

/* Mobile responsiveness */
@media (max-width: 600px) {
  .file-name {
    max-width: calc(100vw - 140px); /* Reserve space for icon, padding, and close button */
  }
}

.upload-progress-container {
  border: 1px solid rgb(var(--v-theme-outline-variant));
  border-radius: 4px;
  padding: 12px;
}

.upload-progress-text {
  font-size: 14px;
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
}

.upload-icon {
  width: 64px;
  height: 64px;
}

/* File container for topic selection step */
.file-container {
  border-radius: 4px;
  padding: 8px;
}

.file-preview {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  overflow: hidden;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.file-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.error-file-list {
  margin: 0;
  padding-left: 0.5rem;
}
</style>
